<template>
  <div>
    <v-progress-linear
      v-if="loading"
      indeterminate
    />
    <v-card>
      <v-toolbar
        flat
        color="primary"
        dark
        dense
      >
        <v-icon left>mdi-message-question</v-icon>
        Support &amp; Defaults
        <v-spacer />
      </v-toolbar>
      <v-expand-transition>
        <v-card-text
          v-if="!loading"
        >
          <v-autocomplete
            v-model="orgSettings.default_receiver"
            :items="users"
            :filter="customFilter"
            item-value="id"
            :search-input.sync="searchUsers"
            label="Default Contact Message User"
            hint="Default user to send messages from contact notifications to"
            auto-select-first
            @input="searchUsers = ''"
          >
            <template v-slot:item="{ item }">
              <v-list-tile-content>
                <v-list-tile-title>{{item.first_name}} {{item.last_name}}</v-list-tile-title>
                <v-list-tile-sub-title>{{item.email}}</v-list-tile-sub-title>
              </v-list-tile-content>
            </template>
            <template v-slot:selection="{ item }">
              <v-card-text>
                <v-list-tile-content>
                  <v-list-tile-title>{{item.first_name}} {{item.last_name}}</v-list-tile-title>
                  <span style="font-size:10px;line-height:12px;text-transform:none;">{{item.email}}</span>
                </v-list-tile-content>
              </v-card-text>
            </template>
          </v-autocomplete>
          
          <v-text-field
            v-model="orgSettings.support_email"
            label="Organization Support E-mail"
            hint="Displayed if a contact requests support or clicks on 'Get Help' links and for DocuSign replies (if unset)"
          />
          <v-text-field
            v-model="orgSettings.support_phone"
            label="Organization Support Phone Number"
            hint="Displayed if a contact requests support or clicks on 'Get Help' links"
          />
          <br />
        </v-card-text>
      </v-expand-transition>
      <v-card-actions>
        <v-spacer />
        <v-btn
          :disabled="loading"
          color="primary"
          @click="save"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import api from '@/api'
import Editor from '@/components/admin/Editor'
import { Sketch } from 'vue-color'
import appMixin from '@/components/global/appmixin.js'

export default {
  name: 'OrgCommunicationDefaults',
  mixins: [
    appMixin
  ],
  components: {
    Editor,
    'sketch-picker': Sketch,
  },
  data () {
    return {
      loading: false,
      orgSettings: {},
      uploadDialog: false,
      uploadIcon: false,
      files: '',
      searchUsers: '',
      users: [],
      isAnImage: true,
      nonCompatibleImageTypes: [
        'tiff',
        'svg+xml'
      ],
      invalidFileMessage: `File is not compatible <br /> Please upload a PNG, JPEG, or GIF`
    }
  },
  created () {
    this.documentTitle('Organization Settings')
    this.fetch()
  },
  watch: {
    searchUsers () {
      if (this.searchUsers) {
        this.fetchUsers()
      }
    }
  },
  methods: {
    fetch () {
      this.loading = true
      api.auth.organization.get()
        .then(org => {
          this.orgSettings = org
          delete this.orgSettings.logo
          delete this.orgSettings.icon
          if (org.default_receiver) {
            this.fetchUsers(org.default_receiver)
          }
          this.loading = false
        })
    },
    fetchUsers (id = null) {
      if (id) {
        api.users.user.get(id)
          .then(data => {
            this.users = [data]
          })
      } else {
        api.users.user.list({
          search: this.searchUsers, count: 10
        })
          .then(data => {
            this.users = data.results
          })
      }
    },
    customFilter (item, queryText, itemText) {
      const lname = item.last_name.toLowerCase()
      const fname = item.first_name.toLowerCase()
      const email = item.email.toLowerCase()
      const searchText = queryText.toLowerCase()

      return lname.indexOf(searchText) > -1 ||
        fname.indexOf(searchText) > -1 ||
        email.indexOf(searchText) > -1
    },
    save () {
      this.failed = false
      this.loading = true
      api.auth.organization.save(this.orgSettings)
        .then(() => {
          this.$router.go()
        })
        .catch(() => {
          alert('Something went wrong attempting to update organization settings.')
          this.loading = false
        })
    },
    updatePrimary (value) {
      this.orgSettings.primary_color = value.hex8
      try {
        var element = document.querySelector(".main_app_toolbar")
        element.className = element.className.replace(/\bmain_app_toolbar\b/g, "main_app_toolbar_live")
      }
      catch (err) {
        // do nothing
      }
      document.querySelector(".main_app_toolbar_live").style.background = value.hex8;
    },
    updateSecondary (value) {
      this.orgSettings.secondary_color = value.hex8
      document.querySelector(".logo_back").style.background = value.hex8;
    },
    handleFileUploads () {
      this.isAnImage = true
      this.files = this.$refs.files.files
    },
    submitFiles () {
      if(this.isFileAnImage(this.files[0])){
        this.isAnImage = true
        this.loading = true
        let formData = new FormData()
        for( var i = 0; i < this.files.length; i++ ){
          let file = this.files[i]
          formData.append(`upload`, file)
        }
        api.auth.organization.updateImage(formData, this.uploadIcon ? 'icon' : 'logo')
          .then(response => {
            this.$router.go()
          })
          .catch(err => {
            this.loading = false
            alert('Something went wrong attempting to save that image.')
          })
      } else {
        this.isAnImage = false
      }
    },
    downloadBounceCsv () {
      this.loading = true
      api.email.problems.downloadCsv()
        .then(response => {
          this.loading = false
          let blob = new Blob([response.data])
          let link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob)
          link.download = 'Export_ContactEmailProblems_Report' + '.csv'
          link.click()
        })
    },
    isFileAnImage (file) {
      return file.type.split('/')[0] === 'image' && !this.nonCompatibleImageTypes.includes(file.type.split('/')[1]) 
    }
  }
}
</script>

<style scoped>
  .details span {
    display:block;
    padding:3px;
  }

  .uploadHover {
    position: absolute;
    padding:50px;
    width:100%;
    height:100%;
    top:40px;
    bottom:0;
    background:rgba(255,255,255,0);
    opacity:0.05;
  }

  .uploadHover span {
    font-size:18px;
    text-align:center;
  }

  .uploadHover:hover {
    opacity:1;
    background:rgba(255,255,255,0.5);
  }
</style>
